import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleById, getRelatedArticles } from '@/mocks/articleData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' } as ScrollToOptions);
  }, [slug]);

  const article = getArticleById(slug || '');

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen bg-background-50 pt-24 pb-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的文章不存在或已被移除。</p>
            <Link
              to="/category/features"
              className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article.id, article.category);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      "@type": "Person",
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      "@type": "Organization",
      name: "hlw 平台",
      logo: {
        "@type": "ImageObject",
        url: "https://readdy.ai/api/search-image?query=Modern%20minimalist%20technology%20brand%20logo%20design%20with%20abstract%20geometric%20shapes%2C%20warm%20beige%20and%20cream%20tones%2C%20clean%20professional%20tech%20identity&width=120&height=120&seq=hlw-logo&orientation=squarish",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.hlw.com/article/${article.id}`,
    },
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={`hlw,${article.tags.join(',')},${article.categoryLabel}`}
        canonicalUrl={`/article/${article.id}`}
        ogType="article"
        ogImage={article.coverImage}
        schemaJson={articleSchema}
        articlePublishedTime={article.date}
      />
      <Navbar />
      <main className="min-h-screen bg-background-50">
        <article className="max-w-4xl mx-auto px-4 md:px-6 pt-24 pb-16">
          <Breadcrumb
            items={[
              { label: t('breadcrumb.home'), href: '/' },
              { label: article.categoryLabel, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <div className="mt-6">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                {article.readTime}
              </span>
            </div>

            <div className="aspect-video rounded-lg overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-lg max-w-none prose-headings:text-foreground-950 prose-p:text-foreground-700 prose-p:leading-relaxed prose-h2:text-xl md:prose-h2:text-2xl prose-h2:font-bold prose-h2:mt-8 prose-h2:mb-4"
              dangerouslySetInnerHTML={{ __html: article.contentHtml }}
            />
          </div>

          {relatedArticles.length > 0 && (
            <section className="mt-16 pt-10 border-t border-background-200/70">
              <h2 className="text-xl font-bold text-foreground-950 mb-6">
                {t('article.relatedArticles')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group p-4 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                  >
                    <h3 className="text-sm font-semibold text-foreground-950 mb-1 group-hover:text-primary-500 transition-colors line-clamp-2">
                      {ra.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-1">{ra.excerpt}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}